import React, {useEffect, useRef, useState} from "react";
import {T} from "@tolgee/react";
import {TwoFactorMethodType} from "@evennt-tech/api-v2-external";
import {completeGoogleSignIn} from "tools/auth/completeGoogleSignIn";
import {
  GoogleCallbackStatus,
  GoogleSignInReason,
  GoogleSignInReturnPath,
  readGoogleCallbackResult,
  resolveGoogleErrorKey,
  startGoogleSignIn,
} from "tools/auth/googleSignIn";
import {APP_PATH} from "view/routes";

type TwoFactorState = {
  pendingToken: string;
  method: TwoFactorMethodType;
  methods: TwoFactorMethodType[];
};

type Options = {
  /** The page the invite is being accepted on — carried in the signed state. */
  returnPath: GoogleSignInReturnPath;
  /** Where the person lands once the session starts. */
  redirectTo?: string;
};

/**
 * Accepting an invite with Google, shared by the two sign-up pages. The invite
 * token already in the page URL rides along in the OAuth state, so the callback
 * can validate it and activate the account on return.
 */
export const useGoogleInviteSignUp = ({returnPath, redirectTo}: Options) => {
  const [isBusy, setIsBusy] = useState(false);
  const [googleError, setGoogleError] = useState<React.ReactNode>(null);
  const [twoFactor, setTwoFactor] = useState<TwoFactorState | null>(null);
  // The link died between the page opening and the return from Google — the
  // page shows the same invalid-link card it shows on arrival.
  const [isInviteInvalid, setIsInviteInvalid] = useState(false);

  // The exhibitor page resolves its quick-cart target from the invite token
  // after the first render, so read the latest value at call time.
  const redirectToRef = useRef(redirectTo);
  redirectToRef.current = redirectTo;

  const isHandlingCallbackRef = useRef(false);

  const completeSignIn = (customToken: string) =>
    completeGoogleSignIn(customToken, redirectToRef.current || APP_PATH);

  useEffect(() => {
    const result = readGoogleCallbackResult();

    if (!result || isHandlingCallbackRef.current) return;

    isHandlingCallbackRef.current = true;

    if (result.status === GoogleCallbackStatus.TwoFactor) {
      setTwoFactor({
        pendingToken: result.pendingToken,
        method: result.method,
        methods: result.methods,
      });
      isHandlingCallbackRef.current = false;
      return;
    }

    // Only the embedded show sign-up asks for identity mode, so this cannot
    // arrive here — but the union includes it, and silently falling through to
    // the failure branch would show a misleading message if it ever did.
    if (result.status === GoogleCallbackStatus.Identity) {
      setGoogleError(<T keyName={resolveGoogleErrorKey(null)} />);
      isHandlingCallbackRef.current = false;
      return;
    }

    if (result.status === GoogleCallbackStatus.Success) {
      setIsBusy(true);
      completeSignIn(result.customToken).catch((error) => {
        console.error(error);
        setGoogleError(<T keyName={resolveGoogleErrorKey(null)} />);
        setIsBusy(false);
        isHandlingCallbackRef.current = false;
      });
      return;
    }

    if (result.reason === GoogleSignInReason.InviteEmailMismatch) {
      setGoogleError(
        <T
          keyName="global.sign_up.error.google_invite_email_mismatch"
          params={{email: result.invitedEmail ?? ""}}
        />
      );
    } else if (result.reason === GoogleSignInReason.InviteInvalid) {
      setIsInviteInvalid(true);
    } else {
      setGoogleError(<T keyName={resolveGoogleErrorKey(result.reason)} />);
    }
    isHandlingCallbackRef.current = false;
    // Runs once per mount — `readGoogleCallbackResult` consumes and strips the
    // fragment it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startGoogleSignUp = async () => {
    try {
      setGoogleError(null);
      setIsBusy(true);
      await startGoogleSignIn(returnPath);
    } catch (error) {
      console.error(error);
      setGoogleError(<T keyName={resolveGoogleErrorKey(null)} />);
      setIsBusy(false);
    }
  };

  return {
    isBusy,
    googleError,
    isInviteInvalid,
    twoFactor,
    startGoogleSignUp,
    completeSignIn,
    resetTwoFactor: () => setTwoFactor(null),
    /** True while the page is finishing a callback and should not render its form. */
    isCompletingCallback: isBusy && !googleError,
  };
};

export default useGoogleInviteSignUp;
